import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { toast } from './shared/utils/toast';
import { Observable, catchError } from 'rxjs';

/**
 * Opt-out for the generic 403 toast. Set this on requests whose callers
 * visibly handle authorization errors themselves (inline error states,
 * error-page redirects, expected-failure background calls), so users don't
 * get a duplicate generic toast on top of the specific handling.
 */
export const SKIP_AUTH_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

/** Convenience for `{ context: skipAuthErrorToast() }` request options. */
export function skipAuthErrorToast(
  context: HttpContext = new HttpContext()
): HttpContext {
  return context.set(SKIP_AUTH_ERROR_TOAST, true);
}

/** Set on a request's HttpContext to suppress ALL global error toasts —
 * for background calls whose failure the user should not be bothered with. */
export const SKIP_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

export const httpErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  return next(req).pipe(
    catchError((error) => {
      if (!(error instanceof HttpErrorResponse)) {
        throw error;
      }

      if (req.context.get(SKIP_ERROR_TOAST)) {
        throw error;
      }

      switch (error.status) {
        case 400: {
          toast.error('Ongeldige invoer.', { duration: 10000 });
          throw error;
        }

        // 401 is deliberately NOT toasted: it predominantly occurs in
        // background auth flows that handle it themselves (account linking,
        // silent token refresh, invite accept before login), and at this
        // point in the chain we cannot reliably tell those apart from a
        // genuine expired-session failure.
        case 403: {
          if (!req.context.get(SKIP_AUTH_ERROR_TOAST)) {
            toast.error('Je hebt geen rechten voor deze actie.', {
              duration: 10000,
            });
          }
          throw error;
        }

        case 503: {
          const message =
            error.error?.message ||
            'De dienst is tijdelijk niet beschikbaar. Probeer het later opnieuw.';
          toast.error(message, { duration: 10000 });
          throw error;
        }

        default: {
          if (error.status >= 500) {
            toast.error('Er ging iets mis. Probeer het later opnieuw.', {
              duration: 10000,
            });
          }
          throw error;
        }
      }
    })
  );
};
